import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  subscribeDebugLog,
  getDebugLog,
  clearDebugLog,
  subscribeDebugUi,
  getDebugUi,
  setDebugUi,
  type DebugEntry,
} from '../utils/debugLog';
import { useAuth } from '../hooks/useAuth';
import { KeepAliveBridge } from '../native/KeepAliveBridge';
import { Z_INDEX } from '../constants/zIndex';

/**
 * Always-mounted diagnostics overlay. A small corner target (long-press ~700ms to
 * open, so residents don't trigger it) reveals a full-screen panel with device
 * ids, auth/provisioning state, current route, and the live in-app log buffer.
 *
 * Reachable on EVERY screen incl. /login and /admin-setup and during a
 * provisioning loop — unlike the auth-gated routes. z-index sits below the call
 * screens (9990) so an incoming call still surfaces over it.
 */
export function DebugOverlay() {
  // Visibility is controlled remotely per-device via the MQTT `debug_overlay`
  // command (setDebugUi). Hidden by default so residents never see it. Both
  // `enabled` and `open` are persisted, so they survive reloads / a reload loop.
  const ui = useSyncExternalStore(subscribeDebugUi, getDebugUi);
  const open = ui.open;
  const setOpen = (v: boolean) => setDebugUi({ open: v });
  const entries = useSyncExternalStore(subscribeDebugLog, getDebugLog);
  const { isAuthenticated, accessToken, user } = useAuth() as {
    isAuthenticated: boolean;
    accessToken?: string | null;
    user?: { firstName?: string; lastName?: string; locationName?: string } | null;
  };
  const location = useLocation();
  const [ids, setIds] = useState<{ androidId: string; managedUdid: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const pressTimer = useRef<number | null>(null);
  const logEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      void KeepAliveBridge.getDeviceIds().then(setIds).catch(() => {});
    }
  }, [open]);

  // Auto-scroll to newest line when open.
  useEffect(() => {
    if (open) logEndRef.current?.scrollIntoView({ block: 'end' });
  }, [open, entries.length]);

  const startPress = () => {
    pressTimer.current = window.setTimeout(() => setOpen(true), 700);
  };
  const cancelPress = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const handleCopy = async () => {
    const text = entries.map((e) => line(e)).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  // Hidden unless remotely enabled (MQTT debug_overlay command) for this device.
  if (!ui.enabled) return null;

  if (!open) {
    return (
      <div
        onPointerDown={startPress}
        onPointerUp={cancelPress}
        onPointerLeave={cancelPress}
        role="button"
        aria-label="Diagnostics (long-press)"
        style={{
          position: 'fixed',
          left: 6,
          bottom: 6,
          width: 38,
          height: 38,
          borderRadius: 19,
          background: 'rgba(0,0,0,0.15)',
          zIndex: Z_INDEX.DEBUG_OVERLAY_TOGGLE,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          color: 'rgba(255,255,255,0.5)',
          userSelect: 'none',
          touchAction: 'none',
        }}
      >
        🐞
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_INDEX.DEBUG_OVERLAY_PANEL,
        background: '#0b0f14',
        color: '#d6dee6',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
        fontSize: 12,
      }}
    >
      {/* Header + state summary */}
      <div style={{ padding: '10px 12px', borderBottom: '1px solid #1e2a36', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <strong style={{ fontSize: 14, color: '#fff' }}>Diagnostics</strong>
          <span style={{ flex: 1 }} />
          <button onClick={handleCopy} style={btn}>{copied ? 'Copied ✓' : 'Copy'}</button>
          <button onClick={() => clearDebugLog()} style={btn}>Clear</button>
          <button onClick={() => setOpen(false)} style={{ ...btn, background: '#7a2f2f' }}>Close ✕</button>
        </div>
        <Row k="platform" v={Capacitor.isNativePlatform() ? `native (${Capacitor.getPlatform()})` : 'web'} />
        <Row k="route" v={location.pathname + location.search} />
        <Row k="authenticated" v={String(isAuthenticated)} />
        <Row k="hasToken" v={String(Boolean(accessToken))} />
        <Row k="user" v={user ? `${user.firstName ?? ''} ${user.lastName ?? ''} · ${user.locationName ?? '-'}`.trim() : '(none)'} />
        <Row k="ANDROID_ID" v={ids ? ids.androidId || '(empty)' : '…'} />
        <Row k="Managed UDID" v={ids ? ids.managedUdid || '(not pushed)' : '…'} />
      </div>

      {/* Log list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px', lineHeight: 1.45 }}>
        {entries.length === 0 && <div style={{ opacity: 0.5 }}>(no log entries yet)</div>}
        {entries.map((e) => (
          <div key={e.id} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: colorFor(e.level) }}>
            {line(e)}
          </div>
        ))}
        <div ref={logEndRef} />
      </div>
    </div>
  );
}

const btn: React.CSSProperties = {
  background: '#1e2a36',
  color: '#d6dee6',
  border: 'none',
  borderRadius: 6,
  padding: '6px 10px',
  fontSize: 12,
  cursor: 'pointer',
};

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ color: '#6b8299', minWidth: 96 }}>{k}</span>
      <span style={{ color: '#fff', wordBreak: 'break-all' }}>{v}</span>
    </div>
  );
}

function colorFor(level: DebugEntry['level']): string {
  switch (level) {
    case 'error': return '#ff6b6b';
    case 'warn': return '#ffcf5c';
    case 'event': return '#5cc6ff';
    default: return '#a9b7c6';
  }
}

function line(e: DebugEntry): string {
  const ts = new Date(e.t).toLocaleTimeString([], { hour12: false });
  return `${ts} ${e.level.toUpperCase().padEnd(5)} ${e.msg}`;
}
